import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { UserPlus } from "lucide-react";
import { tanggalWaktu } from "@/lib/format";

const ROLES = [
  { value: "super_admin", label: "Admin (Super Admin)", desc: "Akses penuh semua menu & pengaturan" },
  { value: "finance_admin", label: "Editor Finance (Finance Admin)", desc: "Kelola PO, kontrak, tarif, proforma, draft invoice, pembayaran" },
  { value: "finance_approver", label: "Approver Finance", desc: "Setujui & terbitkan proforma/invoice, approve pembatalan" },
  { value: "admin_logistik", label: "Admin Logistik", desc: "Surat jalan, pengembalian, saldo material" },
  { value: "viewer", label: "Viewer (Hanya Lihat)", desc: "Lihat dashboard, invoice, piutang, laporan" },
];
const roleLabel = (v) => ROLES.find((r) => r.value === v)?.label || v;

export default function Users() {
  const { data, loading, reload } = useFetch("/users");
  const [sel, setSel] = useState(null);
  const [role, setRole] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", role: "viewer" });
  const [inviting, setInviting] = useState(false);

  const open = (u) => { setSel(u); setRole(u.role); setActive(u.active !== false); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${sel.user_id}/role`, { role });
      if ((sel.active !== false) !== active) await api.put(`/users/${sel.user_id}/active`, { active });
      toast.success("Pengguna diperbarui"); setSel(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal (hanya Super Admin)"); }
    finally { setSaving(false); }
  };

  const sendInvite = async () => {
    if (!invite.email || !invite.email.includes("@")) { toast.error("Masukkan email yang valid"); return; }
    setInviting(true);
    try {
      await api.post("/users/invite", invite);
      toast.success(`Undangan dibuat untuk ${invite.email}. Karyawan cukup login Google dengan email tsb.`);
      setInviteOpen(false); setInvite({ email: "", role: "viewer" }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengundang"); }
    finally { setInviting(false); }
  };

  return (
    <div>
      <PageHeader title="Pengguna" subtitle="Kelola hak akses & undang karyawan (Editor/Viewer). Hanya Super Admin."
        action={<Button onClick={() => setInviteOpen(true)} data-testid="invite-user-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]"><UserPlus className="h-4 w-4 mr-1" /> Undang Pengguna</Button>} />
      {loading ? <div className="h-64 rounded-md bg-muted animate-pulse" /> : (
        <DataTable testId="users" data={data || []} onRowClick={open} columns={[
          { key: "name", label: "Nama" },
          { key: "email", label: "Email" },
          { key: "role", label: "Role", render: (r) => roleLabel(r.role) },
          { key: "active", label: "Status", render: (r) => {
            if (r.invited && !r.picture) return <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border-amber-200">Diundang · Menunggu Login</span>;
            const on = r.active !== false;
            return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${on ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{on ? "Aktif" : "Nonaktif"}</span>;
          } },
          { key: "created_at", label: "Ditambahkan", render: (r) => tanggalWaktu(r.created_at) },
        ]} />
      )}

      {/* Invite Sheet */}
      <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto scrollbar-thin">
          <SheetHeader><SheetTitle className="font-display">Undang Pengguna</SheetTitle></SheetHeader>
          <div className="py-6 space-y-4">
            <p className="text-xs text-muted-foreground">Masukkan email Google karyawan dan pilih perannya. Setelah diundang, karyawan cukup klik "Lanjut dengan Google" memakai email tersebut untuk langsung mendapat akses sesuai role.</p>
            <div className="space-y-1.5"><Label>Email Karyawan</Label>
              <Input type="email" placeholder="nama@cvpcm.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" />
            </div>
            <div className="space-y-1.5"><Label>Peran (Role)</Label>
              <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">{ROLES.find((r) => r.value === invite.role)?.desc}</p>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Batal</Button>
            <Button onClick={sendInvite} disabled={inviting} data-testid="invite-send-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]">{inviting ? "Mengundang..." : "Kirim Undangan"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Sheet */}
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-md">
          {sel && (<>
            <SheetHeader><SheetTitle className="font-display">{sel.name}</SheetTitle></SheetHeader>
            <div className="py-6 space-y-4">
              <div className="text-sm text-muted-foreground">{sel.email}</div>
              <div className="space-y-1.5"><Label>Peran (Role)</Label>
                <Select value={role} onValueChange={setRole}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
                <p className="text-[11px] text-muted-foreground">{ROLES.find((r) => r.value === role)?.desc}</p>
              </div>
              <div className="flex items-center justify-between border-t pt-4">
                <div><div className="font-medium text-sm">Akun Aktif</div><div className="text-xs text-muted-foreground">Nonaktifkan untuk mencabut akses login</div></div>
                <Switch checked={active} onCheckedChange={setActive} data-testid="user-active-switch" />
              </div>
            </div>
            <SheetFooter><Button variant="outline" onClick={() => setSel(null)}>Batal</Button><Button onClick={save} disabled={saving} data-testid="user-save-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]">{saving ? "..." : "Simpan"}</Button></SheetFooter>
          </>)}
        </SheetContent>
      </Sheet>
    </div>
  );
}
